import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './BecomeMember.css'
import { db } from '../firebase'
import Logo from '../assets/Logo.jpg'
import Logo1 from '../assets/logo1.jpg'
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { initializeRecaptchaVerifier, normalizeIndianPhoneNumber, sendSmsOtp, verifySmsOtp } from '../api/otpClient'

export default function BecomeMember(){
  const [form, setForm] = useState({ name:'', dob:'', district:'', state:'', phone:'', email:'', photoData: '' })
  const [step, setStep] = useState('form')
  const [otp, setOtp] = useState('')
  const [message, setMessage] = useState('')
  const [generatedId, setGeneratedId] = useState('')
  const [verifiedPhone, setVerifiedPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState(null)
  const navigate = useNavigate()
  const cardRef = useRef(null)

  useEffect(()=>{
    if(window.location.hostname === 'localhost'){
      window.location.replace(window.location.href.replace('localhost', '127.0.0.1'))
    }
  },[])

  useEffect(()=>{
    let mounted = true
    initializeRecaptchaVerifier().catch((error)=>{
      if(mounted) setMessage(error.message || 'Unable to initialize phone verification.')
    })
    return ()=>{ mounted = false }
  },[])

  useEffect(()=>{
    if(!toast) return undefined
    const timer = window.setTimeout(()=>setToast(null), 5000)
    return ()=>window.clearTimeout(timer)
  },[toast])

  const handleChange = (e)=>{
    const { name, value } = e.target
    setForm((prev)=>({...prev, [name]: value}))
    if(name === 'phone'){
      setStep('form')
      setOtp('')
      setVerifiedPhone('')
    }
  }

  const handleFile = (e)=>{
    const file = e.target.files && e.target.files[0]
    if(!file) return
    if(file.size > 2 * 1024 * 1024){
      setMessage('Photo should be less than 2MB for clear export and faster upload.')
      return
    }
    const reader = new FileReader()
    reader.onload = (ev)=>setForm((prev)=>({...prev, photoData: ev.target.result}))
    reader.readAsDataURL(file)
  }

  const loadExistingMembers = async ()=>{
    const col = collection(db, 'members')
    const snap = await getDocs(col)
    return snap.docs.map((doc)=>({ id: doc.id, ...doc.data() }))
  }

  const nextMemberId = (members)=>{
    const year = new Date().getFullYear()
    const used = new Set(members.map((member)=>Number(String(member.idNumber || member.memberId || '').split('-').pop())))
    let next = 1
    while(used.has(next)) next += 1
    return `PF-${year}-${String(next).padStart(3,'0')}`
  }

  const phoneAlreadyRegistered = async (phone)=>{
    const col = collection(db, 'members')
    const q = query(col, where('phone', '==', phone))
    const snap = await getDocs(q)
    return !snap.empty
  }

  const sendOtp = async (e)=>{
    e.preventDefault()
    const normalizedPhone = normalizeIndianPhoneNumber(form.phone)
    if(!normalizedPhone){ setMessage('Enter a valid 10-digit Indian mobile number.'); return }

    setLoading(true)
    setMessage('Sending SMS verification code...')
    try{
      if(await phoneAlreadyRegistered(normalizedPhone)){
        setMessage('This mobile number is already registered with the foundation.')
        return
      }
      await sendSmsOtp(normalizedPhone)
      setStep('otp')
      setMessage('A six-digit verification code has been sent by SMS.')
    }catch(err){
      console.error('sendOtp error', err)
      setMessage(err.message || 'We could not send the SMS right now. Please try again.')
    }finally{
      setLoading(false)
    }
  }

  const verifyOtp = async (e)=>{
    e.preventDefault()
    if(!/^\d{6}$/.test(otp.trim())){ setMessage('Enter the 6-digit OTP from the SMS.'); return }

    const normalizedPhone = normalizeIndianPhoneNumber(form.phone)
    setLoading(true)
    setMessage('Verifying OTP...')
    try{
      await verifySmsOtp(normalizedPhone, otp.trim())
      setVerifiedPhone(normalizedPhone)
      setStep('email')
      setMessage('Phone number verified. Enter email to complete registration.')
    }catch(err){
      console.error('verifyOtp error', err)
      setMessage(err.message || 'OTP verification failed. Please try again.')
    }finally{
      setLoading(false)
    }
  }

  const saveMember = async () =>{
    if(!verifiedPhone || verifiedPhone !== normalizeIndianPhoneNumber(form.phone)){
      setMessage('Please verify your phone number before creating the member record.')
      return
    }

    setLoading(true)
    setMessage('Creating member record...')
    try{
      const existingMembers = await loadExistingMembers()
      const memberId = nextMemberId(existingMembers)
      const newDoc = {
        name: form.name.trim(),
        dob: form.dob,
        district: form.district.trim(),
        state: form.state.trim(),
        phone: verifiedPhone,
        email: form.email.trim(),
        photoData: form.photoData || '',
        idNumber: memberId,
        memberId,
        createdAt: new Date().toISOString()
      }
      await addDoc(collection(db, 'members'), newDoc)
      setGeneratedId(memberId)
      setStep('created')
      const successText = 'Registration successful. Your Pramila Foundation member ID card is ready.'
      setMessage(successText)
      setToast(successText)
      setTimeout(()=>cardRef.current?.scrollIntoView({behavior:'smooth', block:'center'}),300)
    }catch(err){
      console.error('saveMember error', err)
      setMessage('Save failed: '+err.message)
    }finally{
      setLoading(false)
    }
  }

  const exportCanvas = async ()=>{
    const el = document.getElementById('id-card')
    if(!el) return null
    return html2canvas(el, { backgroundColor: '#ffffff', scale: 3, useCORS: true })
  }

  const downloadImage = async ()=>{
    const canvas = await exportCanvas()
    if(!canvas) return
    const a = document.createElement('a')
    a.href = canvas.toDataURL('image/png')
    a.download = `${generatedId || form.name || 'member'}-id.png`
    a.click()
  }

  const downloadPDF = async ()=>{
    const canvas = await exportCanvas()
    if(!canvas) return
    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [canvas.width, canvas.height] })
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height)
    pdf.save(`${generatedId || form.name || 'member'}-id.pdf`)
  }

  return (
    <section className="become-member-page">
      <div className="container">
        <div className="outer-panel">
          <div className="panel-inner">
            <h2>Become a Member</h2>
            <p className="muted">Join Pramila Foundation with verified SMS registration and get your member ID card.</p>

            {step === 'form' && (
              <form className="member-form" onSubmit={sendOtp}>
                <label>Name<input name="name" value={form.name} onChange={handleChange} required /></label>
                <label>DOB<input name="dob" type="date" value={form.dob} onChange={handleChange} required /></label>
                <label>District<input name="district" value={form.district} onChange={handleChange} required /></label>
                <label>State<input name="state" value={form.state} onChange={handleChange} required /></label>
                <label>Phone<input name="phone" inputMode="tel" value={form.phone} onChange={handleChange} placeholder="10-digit mobile number" required /></label>
                <label>Photo<input name="photo" type="file" accept="image/*" onChange={handleFile} /></label>
                <div className="form-actions"><button type="submit" className="primary" disabled={loading}>{loading ? 'Sending...' : 'Send SMS OTP'}</button></div>
                {message && <p className="message">{message}</p>}
              </form>
            )}

            {step === 'otp' && (
              <form className="member-form single-column" onSubmit={verifyOtp}>
                <label>Enter OTP<input name="otp" inputMode="numeric" maxLength="6" value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,'').slice(0,6))} required /></label>
                <div className="form-actions"><button type="submit" className="primary" disabled={loading}>{loading ? 'Verifying...' : 'Verify OTP'}</button></div>
                {message && <p className="message">{message}</p>}
              </form>
            )}

            {step === 'email' && (
              <form className="member-form single-column" onSubmit={async (e)=>{ e.preventDefault(); await saveMember(); }}>
                <label>Email<input name="email" type="email" value={form.email} onChange={handleChange} required /></label>
                <div className="form-actions"><button type="submit" className="primary" disabled={loading}>{loading ? 'Creating...' : 'Create Member ID Card'}</button></div>
                {message && <p className="message">{message}</p>}
              </form>
            )}

            {step === 'created' && (
              <div className="created-section">
                <div id="id-card" className="id-card" ref={cardRef}>
                  <div className="id-header">
                    <img src={Logo} alt="Pramila Foundation logo" className="id-logo"/>
                    <div className="id-title-wrap">
                      <h3>PRAMILA FOUNDATION</h3>
                      <p>Member Identity Card</p>
                    </div>
                    <img src={Logo1} alt="Pramila Foundation emblem" className="id-logo-right"/>
                  </div>
                  <div className="id-photo">
                    {form.photoData ? <img src={form.photoData} alt="member" /> : <div className="photo-placeholder">Photo</div>}
                  </div>
                  <div className="id-info">
                    <h4>{form.name}</h4>
                    <p><strong>Member ID</strong><span>{generatedId}</span></p>
                    <p><strong>DOB</strong><span>{form.dob}</span></p>
                    <p><strong>District</strong><span>{form.district}</span></p>
                    <p><strong>State</strong><span>{form.state}</span></p>
                    <p><strong>Phone</strong><span>{verifiedPhone}</span></p>
                    <p><strong>Email</strong><span>{form.email}</span></p>
                  </div>
                  <div className="id-footer">Verified by Pramila Foundation</div>
                </div>
                <div className="actions">
                  <button onClick={downloadImage}>Download Image</button>
                  <button onClick={downloadPDF}>Download PDF</button>
                  <button onClick={()=>navigate('/foundation-members')}>View Members</button>
                </div>
                {message && <p className="message success-inline">{message}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {toast && <div className="success-toast" role="status">{toast}</div>}
    </section>
  )
}


